import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import useOcapiSite from '../../hooks/useOcapiSite'
import {removeSiteLocaleFromPath} from '../../utils/url'
import PropTypes from 'prop-types'
import useEffectEvent from '../../hooks/use-effect-event'
import logger from '../../utils/logger-instance'
import {useNavigationOpen} from '../../leap/components/navigation/navigation-provider'

const ZendeskChatContext = createContext()

const Z_INDEX_LAUNCHER = 990
const Z_INDEX_CONVERSATION = 1100

const setZendeskZIndex = async (value) => {
    if (!window.zE) return
    try {
        await window.zE('messenger:set', 'zIndex', value)
    } catch (e) {
        logger.error('Failed to set the Zendesk z-index', e)
    }
}

export const ZendeskChatProvider = ({children}) => {
    const location = useLocation()
    const {zendeskChatEnabled, zendeskChatKey, zendeskChatExcludedPages} =
        useOcapiSite().preferences || {}

    const [isLoaded, setIsLoaded] = useState(false)
    const [isVisible, setIsVisible] = useState(true)
    const [isOpened, setIsOpened] = useState(false)
    const [shouldLoad, setShouldLoad] = useState(false)
    const suppressedRef = useRef(false)

    // Which layer the widget belongs on, from both inputs at once. In a ref and
    // applied from the events themselves, so it moves in the same tick as the
    // window instead of a render later. `coveredByNavigation` wins: panel and
    // backdrop share the header bar's 1050 layer, so a conversation raised
    // above the bar would clear them too.
    const layerRef = useRef({conversationOpen: false, coveredByNavigation: false})

    const applyLayer = useEffectEvent(() => {
        const {conversationOpen, coveredByNavigation} = layerRef.current
        setZendeskZIndex(
            conversationOpen && !coveredByNavigation ? Z_INDEX_CONVERSATION : Z_INDEX_LAUNCHER
        )
    })

    // Whether the navigation is currently covering the viewport, read straight
    // from its provider — which `_app` mounts above this one for exactly that.
    // An open conversation drops back under the flyout and its backdrop for as
    // long as the navigation lasts, and returns above the header bar on close.
    const navigationOpen = useNavigationOpen()

    useEffect(() => {
        if (layerRef.current.coveredByNavigation === navigationOpen) return
        layerRef.current.coveredByNavigation = navigationOpen
        applyLayer()
    }, [navigationOpen, applyLayer])

    // Parse excluded pages
    const excludedPages = useMemo(
        () => zendeskChatExcludedPages?.split(',').map((page) => page.trim()) || [],
        [zendeskChatExcludedPages]
    )

    // Determine if Zendesk chat should be active on this page
    const isEnabled = useMemo(() => {
        if (!zendeskChatEnabled || !zendeskChatKey) return false
        if (!excludedPages.length) return true
        const pathWithoutLocale = removeSiteLocaleFromPath(location.pathname)
        return !excludedPages.some((path) => pathWithoutLocale.startsWith(path))
    }, [zendeskChatEnabled, zendeskChatKey, excludedPages, location.pathname])

    useEffect(() => {
        if (isEnabled) setShouldLoad(true)
    }, [isEnabled])

    const STYLE_ID = 'zendesk-offset-style'

    const ensureStyle = () => {
        if (document.getElementById(STYLE_ID)) return
        const style = document.createElement('style')
        style.id = STYLE_ID
        style.textContent = `
            :root { --zendesk-offset: 0px; }

            iframe#launcher {
            bottom: calc(16px + var(--zendesk-offset)) !important;
            }

            /* div с X + Chat */
            [data-zd-actions="1"] {
            bottom: calc(88px + var(--zendesk-offset)) !important;
            }
        `
        document.head.appendChild(style)
    }

    const applyZendeskOffset = (py) => {
        ensureStyle()
        document.documentElement.style.setProperty('--zendesk-offset', py)

        const launcher = document.getElementById('launcher')
        const parent = launcher?.parentElement
        if (!parent) return

        const actions = [...parent.children].find(
            (el) => el.tagName === 'DIV' && el !== launcher && el.style.position === 'fixed'
        )
        if (actions) {
            actions.setAttribute('data-zd-actions', '1')
        }
    }

    const watchZendesk = (py) => {
        applyZendeskOffset(py)

        const obs = new MutationObserver(() => {
            const launcher = document.getElementById('launcher')
            if (launcher) {
                applyZendeskOffset(py)
                obs.disconnect()
            }
        })

        obs.observe(document.body, {childList: true, subtree: true})
        return () => obs.disconnect()
    }

    const setBottomOffset = useEffectEvent((value) => {
        const py = typeof value === 'number' ? `${value}px` : value
        watchZendesk(py)
    })

    // Load Zendesk script
    useEffect(() => {
        if (!isEnabled || !shouldLoad || isLoaded) return

        const script = document.createElement('script')
        script.id = 'ze-snippet'
        script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskChatKey}`
        script.async = true
        script.defer = true
        script.onload = () => {
            const loadTime = Date.now()

            const initializeZendesk = async () => {
                logger.debug(
                    `Zendesk chat loaded in ${Date.now() - loadTime}ms, name: ${
                        window.zE.name
                    }, version: ${window.zE.version}`
                )
                setIsLoaded(true)
                // Kept apart from the call below so a Zendesk change to
                // this setting surfaces as its own error instead of silently
                // taking the conversation field down with it.
                applyLayer()
                try {
                    await window.zE('messenger:set', 'conversationFields', [
                        {id: '46010718065809', value: window.location.href}
                    ])
                } catch (e) {
                    logger.error('Failed to set conversationField 46010718065809', e)
                }
            }

            // Try immediately first
            if (window.zE) {
                initializeZendesk()
                return
            }

            // If not available, poll every 200ms
            const timer = setInterval(async () => {
                if (!window.zE) {
                    if (Date.now() - loadTime > 60000) {
                        logger.error('Zendesk chat failed to load after 60 seconds')
                        clearInterval(timer)
                    }
                    return
                }
                clearInterval(timer)
                await initializeZendesk()
            }, 200)
        }
        script.onerror = () => logger.error('Failed to load Zendesk chat')

        document.body.appendChild(script)
    }, [isEnabled, zendeskChatKey, shouldLoad, isLoaded, applyLayer])

    // Subscribe to Zendesk events
    useEffect(() => {
        if (!isLoaded || !window.zE) return

        const onOpen = () => {
            setIsOpened(true)
            layerRef.current.conversationOpen = true
            applyLayer()
        }
        const onClose = () => {
            setIsOpened(false)
            layerRef.current.conversationOpen = false
            applyLayer()
        }

        window.zE('messenger:on', 'open', onOpen)
        window.zE('messenger:on', 'close', onClose)

        //no cleanup for messenger
    }, [isLoaded, applyLayer])

    // Control visibility when disabled or hidden
    useEffect(() => {
        if (!isLoaded || !window.zE) return

        if (!isEnabled || !isVisible) {
            window.zE('messenger', 'hide')
        } else {
            window.zE('messenger', 'show')
        }
    }, [isLoaded, isEnabled, isVisible])

    // API functions
    const show = useEffectEvent(() => {
        if (suppressedRef.current) return
        setIsVisible(true)
    })
    const hide = useEffectEvent(() => setIsVisible(false))
    const suppress = useEffectEvent(() => {
        suppressedRef.current = true
        setIsVisible(false)
    })
    const unsuppress = useEffectEvent(() => {
        suppressedRef.current = false
    })
    const open = useEffectEvent(() => {
        if (!isLoaded || !window.zE) return
        setIsVisible(true)
        window.zE('messenger', 'open')
    })

    const ctx = useMemo(
        () => ({
            enabled: isEnabled,
            loaded: isLoaded,
            visible: isVisible,
            opened: isOpened,
            show,
            hide,
            suppress,
            unsuppress,
            open,
            setBottomOffset
        }),
        [
            isEnabled,
            isLoaded,
            isVisible,
            isOpened,
            open,
            hide,
            show,
            suppress,
            unsuppress,
            setBottomOffset
        ]
    )

    return <ZendeskChatContext.Provider value={ctx}>{children}</ZendeskChatContext.Provider>
}

ZendeskChatProvider.propTypes = {
    children: PropTypes.node
}

export const useZendeskChat = () => {
    const ctx = useContext(ZendeskChatContext)
    if (!ctx) throw new Error('useZendeskChat must be used within a ZendeskChatProvider')
    return ctx
}
